/**
 * What a system environment's needs look like to the customer, and what an
 * answered need does to the map (contract
 * `docs/superpowers/plans/2026-10-19-explore-claim-fletcher-overnight.md`,
 * "Secrets: the one path").
 *
 * A need is a question only a person can answer: a credential, test data,
 * access to a service outside the copy, or a capability YAGNI does not have
 * yet. Each one becomes an ask on the workspace. An ask carries names and
 * statuses only; a value a person enters is never part of it.
 */
/** A name a value can be placed under in the copy's environment file. */
const ENV_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;
export function isSystemEnvName(value) {
    return typeof value === "string" && value.length <= 200 && ENV_NAME.test(value);
}
/** The longest value a person can enter, the same bound as a Fletcher environment variable. */
export const SYSTEM_NEED_VALUE_MAX_LENGTH = 4096;
/**
 * The shortest: Fletcher's redactor leaves anything shorter alone, so a
 * shorter value placed in a copy could surface unscrubbed in its output.
 */
export const SYSTEM_NEED_VALUE_MIN_LENGTH = 8;
/** Customer words for each capability key, finishing "YAGNI cannot connect to …". */
export const CAPABILITY_WORDS = {
    logs: "your logs",
    errors: "your error tracker",
    tracker: "your issue tracker",
    ci: "your CI",
    feature_flags: "your feature flag service",
    registry: "your package or image registry",
    identity_provider: "your identity provider",
    queue: "your message queue",
    search: "your search service",
    machine_size: "a machine large enough for the whole system",
    other: "a service the system needs",
};
/** Whether a need is answered by typing a value. Credentials always are; test data when it is named as a variable. */
export function needTakesValue(need) {
    if (need.kind === "credential")
        return true;
    return need.kind === "test_data" && isSystemEnvName(need.name);
}
function answerFor(need) {
    if (need.kind === "capability")
        return "none";
    return needTakesValue(need) ? "enter_value" : "mark_provided";
}
/** The ask a person reads for one need. `componentName` is the map's name for the part that needs it. */
export function systemNeedAsk(need, componentName = null) {
    const part = componentName ?? need.componentKey ?? "The system";
    const answer = answerFor(need);
    const base = {
        needId: need.id,
        environmentId: need.environmentId,
        componentKey: need.componentKey,
        kind: need.kind,
        name: need.name,
        status: need.status,
        capabilityKey: need.capabilityKey,
        answer,
        declinable: need.status === "open",
        detail: need.detail,
        provided: need.status === "provided"
            ? {
                repoId: need.secretRepoId,
                revisionId: need.secretRevisionId,
                at: need.resolvedAt,
            }
            : null,
    };
    if (need.status === "provided") {
        return {
            ...base,
            title: `${need.name} is provided`,
            body: answer === "enter_value"
                ? `${part} gets ${need.name} in every copy from now on. The value is stored encrypted, placed in a copy only after it starts, and never shown again.`
                : `${part} counts ${need.name} as answered from the next refresh.`,
        };
    }
    if (need.status === "declined") {
        return {
            ...base,
            title: `${need.name} was declined`,
            body: `${part} stays remote and is named as an edge of the system. Reopen it to answer it.`,
        };
    }
    switch (need.kind) {
        case "credential":
            return {
                ...base,
                title: `Enter ${need.name}`,
                body: `${part} needs ${need.name} to run in the copy. Only a workspace admin can enter it. It is stored encrypted, placed in a copy only after the copy starts, and never written into a snapshot, a log or a prompt.`,
            };
        case "test_data":
            return {
                ...base,
                title: answer === "enter_value"
                    ? `Enter test data ${need.name}`
                    : `Provide test data: ${need.name}`,
                body: answer === "enter_value"
                    ? `${part} needs ${need.name} for its test data. Only a workspace admin can enter it, and it is handled like a credential.`
                    : `${part} needs this test data to run in the copy. Mark it provided once it is in place.`,
            };
        case "reach":
            return {
                ...base,
                title: `Give the copy access: ${need.name}`,
                body: `${part} needs to reach ${need.name} from the copy. Mark it provided once access is granted, or decline it to keep that part remote.`,
            };
        case "capability": {
            const words = need.capabilityKey ? CAPABILITY_WORDS[need.capabilityKey] : "a service";
            return {
                ...base,
                title: `YAGNI cannot connect to ${words} yet`,
                body: `${part} needs ${need.name}. This is on YAGNI's side and is tracked for every workspace that needs it; there is nothing to enter. Decline it to keep that part remote.`,
            };
        }
    }
}
/** The causes a component carries while it waits on a value. */
const WAITING_CAUSES = new Set([
    "credential_missing",
    "test_data_missing",
]);
/** The cause a need of each value kind names. */
const VALUE_KIND_CAUSE = {
    credential: "credential_missing",
    test_data: "test_data_missing",
};
/**
 * What the answers do to the map, the rule Refresh applies before it derives
 * the system's state. Only a component that waits on a value (`needs_you`
 * for `credential_missing` or `test_data_missing`) moves, and only once none
 * of its credential or test data needs is still open (a component carries
 * one cause, so an open need of the other kind still holds it):
 * - every one provided: `working`, because a claim places the values after
 *   the copy starts (Refresh itself never holds them);
 * - any declined: `remote`, a named edge of a hybrid system, with the first
 *   declined need's cause (whichever value kind it is) and every declined
 *   name in the detail.
 * Every other component is returned as it was.
 */
export function applyNeedAnswers(components, needs) {
    return components.map((component) => {
        if (component.state !== "needs_you" || !WAITING_CAUSES.has(component.causeCode)) {
            return component;
        }
        const waiting = needs.filter((need) => need.componentKey === component.key && VALUE_KIND_CAUSE[need.kind] !== undefined);
        if (waiting.length === 0 || waiting.some((need) => need.status === "open"))
            return component;
        const declined = waiting.filter((need) => need.status === "declined");
        if (declined.length > 0) {
            return {
                ...component,
                state: "remote",
                causeCode: VALUE_KIND_CAUSE[declined[0].kind],
                causeDetail: `declined: ${declined.map((need) => need.name).join(", ")}`.slice(0, 2000),
            };
        }
        return { ...component, state: "working", causeCode: null, causeDetail: null };
    });
}
//# sourceMappingURL=systemNeeds.js.map